import React, {useMemo, useRef, useState} from 'react';
import styled from "styled-components";
import CommonText from "@/components/common/Text";
import {getCssSizeValue, getMapKey, isStringEmpty, setCssVars} from "@/utils/utils";
import {gsap, ScrollTrigger, useGSAP} from "@/utils/gsap";
import {BREAKPOINTS} from "@/styles/themeSettings";
import {useArchiveContext} from "@/components/Archive/index";
import useResponsiveValue from "@/hooks/useResponsiveValue";
import useIsomorphicLayoutEffect from "@/hooks/useIsomorphicLayoutEffect";
import CommonButton from "@/components/common/Buttons/Button";
import {BUTTON_VARIANTS, LOCALES} from "@/constants";
import {useIsMobile} from "@/hooks/useDevice";
import {useRouter} from "next/router";
import {useScrollDirection} from "@/hooks/useScrollContext";

const Spacer = styled.div`
    max-height: 100svh;
    max-width: 100vw;
`

const Wrapper = styled.div`
    display: flex;
    width: 100%;
    flex-direction: row;
    z-index: 2;
    grid-column: 1/calc(var(--grid-count) + 1) !important;
    background-color: ${({theme}) => theme.colors.white};
    padding-top: ${getCssSizeValue(90, BREAKPOINTS.MOBILE)};
    padding-left: var(--grid-margin-l);
    padding-right: var(--grid-margin-r);
	gap: ${getCssSizeValue(10, BREAKPOINTS.MOBILE)};
	
    @media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.TABLET]}px) {
        padding-top: ${getCssSizeValue(120, BREAKPOINTS.TABLET)};
    }
    @media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
        grid-column: 1/1 !important;
        flex-direction: column;
        padding: unset;
		
		&.align-bottom {
			height: fit-content; ;
			transform: translateY(${getCssSizeValue(32)});
			justify-content: flex-end;
		}
    }
`

const Container = styled.div`
    position: relative;
    --local-gap: ${getCssSizeValue(10, BREAKPOINTS.MOBILE)};
    display: flex;
	width: 100%;
    align-items: center;
    flex-direction: row;
    gap: var(--local-gap);
    padding-bottom: ${getCssSizeValue(13, BREAKPOINTS.MOBILE)};
	overflow: scroll;
	
    @media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.TABLET]}px) {
        --local-gap: ${getCssSizeValue(20, BREAKPOINTS.TABLET)};
		overflow: visible;
        height: fit-content;
    }

    @media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
        --local-gap: ${getCssSizeValue(10, BREAKPOINTS.DESKTOP)};
		width: unset;
        padding: unset;
        flex-direction: column;
        transform: translateY(-50%);
        align-items: start;
        top: 50svh;
		margin-left: -20px;
		
		&.rotate {
			font-weight: 400;
            transform: translateY(-50%) rotate(-180deg);
            writing-mode: vertical-rl;
        }
    }

    scrollbar-width: none;
    &::-webkit-scrollbar {
        display: none;
    }

`
const Text = styled(CommonText)`
    transition: font-weight var(--rgr-duration) var(--ease);
    cursor: pointer;
	width: 100%;
    flex: 0 0 fit-content;
	
    &.active, &:hover {
        font-weight: 700;
    }
`

const CurrentIndicator = styled.span`
    --current: 0;
    --height: 18;
    display: none;
    position: absolute;
    top: 0;
    left: ${getCssSizeValue(-16)};
    aspect-ratio: 1;
    background-color: ${({theme}) => theme.colors.richBlack};
    transition: transform var(--rgr-duration) var(--ease);
    --move-step: calc(var(--height) * 1px);
    width: ${getCssSizeValue(6, BREAKPOINTS.DESKTOP)};
    translate: 0 calc(-65% + calc(var(--move-step) / 2)); //center vertically
    transform: translateY(calc((var(--move-step) + var(--local-gap)) * var(--current)));

    @media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
        display: block;
    }
`

const Button = styled(CommonButton)`
    z-index: 3;
    text-transform: lowercase;
    background: transparent !important;
    margin-bottom: ${getCssSizeValue(13, BREAKPOINTS.MOBILE)};
	
    @media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.TABLET]}px) {
        margin-left: auto;
        background: initial;
        text-transform: none;
		margin-bottom: unset;
    }

    @media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		padding-bottom: unset;
        margin-top: ${getCssSizeValue(32, BREAKPOINTS.DESKTOP)};
        --spacing-x: ${getCssSizeValue(10, BREAKPOINTS.DESKTOP)} !important;
        left: 0;
		translate: -50% 0;
    }
`

const Timeline = ({data, className = '', onTimeSpanClick, onSkipButtonClick, mainRef, leftSideText = ''}) => {
	const isLeftSideTextEmpty = isStringEmpty(leftSideText)
	const array = useMemo(() => Array.isArray(data) ? data : [], [data])
	const SpacerRef = useRef()
	const WrapperRef = useRef()
	const CurrentIndicatorRef = useRef()
	const current = useArchiveContext('current')
	const TextHeight = useResponsiveValue(32.4) //line-height value of typo-11
	const {locale} = useRouter()
	const [active, setActive] = useState(false)
	const scrollDirection = useScrollDirection()
	const isMobile = useIsMobile()

	useGSAP(() => {
		if (!mainRef) return

		ScrollTrigger.create({
			trigger: mainRef,
			start: 'top top',
			end: 'bottom bottom',
			// endTrigger: WrapperRef.current,
			// end: isDesktop ? 'bottom bottom' : 'top top',
			// markers: true,
			pin: WrapperRef.current,
			pinSpacing: false,
			pinSpacer: SpacerRef.current,
			invalidateOnRefresh: true,
			onUpdate: ({progress, isActive}) => {
				setActive(isActive)
			}
		})
	}, {
		dependencies: [mainRef],
		revertOnUpdate: true,
		scope: WrapperRef.current
	})

	useIsomorphicLayoutEffect(() => {
		const index = array.findIndex((item) => item.date === current)
		setCssVars(CurrentIndicatorRef.current, {
			'current': index
		})
	}, [current, array])

	useIsomorphicLayoutEffect(() => {
		setCssVars(CurrentIndicatorRef.current, {
			'height': TextHeight
		})
	}, [TextHeight])

	useGSAP(() => {
		if (!isMobile) return
		gsap.to(WrapperRef.current, {
			translateY: (active && scrollDirection === 'down') ? '-50%' : 0,
		})
	}, {
		dependencies: [active, scrollDirection, isMobile],
		revertOnUpdate: false
	});

	return (
		<Spacer ref={SpacerRef}>
			<Wrapper ref={WrapperRef} className={`${active ? 'active' : ''} ${!isLeftSideTextEmpty ? 'align-bottom' : ''}`}>
				{isLeftSideTextEmpty ? (
					<>
						<Container>
							{array.map((item, index) => (
								<Text
									key={getMapKey(item, index)}
									className={`typo-borna-11 ${item.date === current ? 'active' : ''}`}
									onClick={() => onTimeSpanClick(item, index)}
								>
									{item?.date || ''}
								</Text>
							))}
							{!isMobile && (
								<Button variant={BUTTON_VARIANTS.SECONDARY} onClick={onSkipButtonClick}>
									{locale === LOCALES.PL ? 'Pomiń' : 'Skip'}
								</Button>
							)}
							<CurrentIndicator ref={CurrentIndicatorRef}/>
						</Container>
						{isMobile && (
							<Button variant={BUTTON_VARIANTS.SECONDARY} onClick={onSkipButtonClick}>
								{locale === LOCALES.PL ? 'Pomiń' : 'Skip'}
							</Button>
						)}
					</>
				) : (
					<Container className={`${!isLeftSideTextEmpty ? 'rotate' : ''} typo-borna typo-borna-47`}>{leftSideText}</Container>
				)}
			</Wrapper>
		</Spacer>
	);
};

export default Timeline;
